import React, { useEffect, useRef, useState } from "react";
import ProductAPI from "../../../api/inventory/ProductApi";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import AuthAPI from "../../../api/authApi";

function Step5Media({ slug, nextStep, prevStep, productId }) {
  if (!productId) {
    productId = sessionStorage.getItem("productId");
  }

  const [images, setImages] = useState([]);
  const [banners, setBanners] = useState([]);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const originalMediaRef = useRef(null);

  /* ---------------- HELPERS ---------------- */
  const getYoutubeID = (url) => {
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&\n?#]+)/,
    );

    return match ? match[1] : null;
  };

  const uploadToS3 = async (file, type) => {
    const meta = {
      assetType: "product",
      name: `${type}-${Date.now()}.${file.type.split("/")[1]}`,
      mimeType: file.type,
      size: file.size,
    };

    const res = await AuthAPI.uploadFile({ files: [meta] });
    const responsesOfFiles = res.data;

    await Promise.all(
      responsesOfFiles.map(async (img, index) => {
        const uploadUrl = img.uploadUrl;
        const key = img.assetUrl;

        // AWS S3 upload
        await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });
      }),
    );

    return responsesOfFiles[0].assetUrl;
  };

  /* ---------------- IMAGE HANDLERS ---------------- */
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    showLoader();

    try {
      const uploaded = [];

      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;

        const key = await uploadToS3(file, "product-image");

        uploaded.push({
          key,
          preview: URL.createObjectURL(file),
        });
      }

      setImages((prev) => [...prev, ...uploaded]);
    } catch (err) {
      showError("Image upload failed");
    } finally {
      hideLoader();
    }

    e.target.value = null;
  };

  /* ---------------- BANNERS HANDLERS ---------------- */
  const handleBannerUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    showLoader();

    try {
      const uploaded = [];

      for (const file of files) {
        // if (!file.type.startsWith("image/")) continue;

        const key = await uploadToS3(file, "product-banners");

        uploaded.push({
          key,
          preview: URL.createObjectURL(file),
          fileType: file.type,
          name: file.name,
        });
      }

      setBanners((prev) => [...prev, ...uploaded]);
    } catch (err) {
      showError("Banner upload failed");
    } finally {
      hideLoader();
    }

    e.target.value = null;
  };

  const removeImage = async (index) => {
    try {
      const res = await AuthAPI.deleteFile({ key: images[index].key });

      if (res.code === 200) {
        await ProductAPI.deleteStep5Media(
          productId,
          "images",
          images[index].key,
        );
      }
    } catch (err) {}

    setImages((prev) => {
      const copy = [...prev];
      const removed = copy.splice(index, 1)[0];
      if (removed?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.preview);
      }
      return copy;
    });
  };

  const removeBanner = async (index) => {
    try {
      const res = await AuthAPI.deleteFile({ key: banners[index].key });

      if (res.code === 200) {
        await ProductAPI.deleteStep5Media(
          productId,
          "banners",
          banners[index].key,
        );
      }
    } catch (err) {}

    setBanners((prev) => {
      const copy = [...prev];
      const removed = copy.splice(index, 1)[0];

      if (removed?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.preview);
      }

      return copy;
    });
  };

  /* ---------------- VIDEO HANDLERS ---------------- */

  const handleVideoUpload = async (e) => {
    setLoading(true);

    const file = e.target.files[0];
    if (!file || !file.type.startsWith("video/")) return;

    try {
      const key = await uploadToS3(file, "product-video");

      setVideo({
        type: "file",
        key,
        preview: URL.createObjectURL(file),
      });
    } catch (err) {
      showError("Video upload failed");
    } finally {
      hideLoader();
      setLoading(false);
      e.target.value = null;
    }
  };

  const handleYoutubeChange = (e) => {
    const url = e.target.value.trim();
    if (!url) {
      setVideo(null);
      return;
    }

    const id = getYoutubeID(url);
    if (!id) {
      showError("Invalid YouTube URL");
      return;
    }

    setVideo({
      type: "youtube",
      url,
      videoId: id,
    });
  };

  const removeVideo = () => {
    if (
      typeof video?.preview === "string" &&
      video.preview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(video.preview);
    }

    setVideo({
      type: "",
      key: "",
      preview: "",
    });
  };

  /* ---------------- FETCH EXISTING MEDIA ---------------- */
  useEffect(() => {
    fetchStep5Media();
  }, []);

  const fetchStep5Media = async () => {
    try {
      const res = await ProductAPI.fetchStep5Media(productId);

      if (res.code === 200) {
        const details = res.data;

        /* ---------------- ORIGINAL SNAPSHOT ---------------- */
        const original = {
          images: details.images?.map((img) => img) || [],
          banners: details.banners?.map((img) => img) || [],
          videos: details.videos?.map((video) => video) || [],
          youtubeUrls: [],
        };

        // handle videos array
        if (Array.isArray(details.videos) && details.videos.length > 0) {
          details.videos.forEach((v) => {
            const url = v || "";
            const isYoutube =
              url.includes("youtube.com") || url.includes("youtu.be");

            if (isYoutube) {
              original.youtubeUrls.push(url);
            } else {
              original.videos.push(v.key || url);
            }
          });
        }

        /* ---------------- UI STATE ---------------- */
        originalMediaRef.current = original;

        // Images preview
        setImages(
          (details.images || []).map((img) => ({
            key: img,
            preview: img,
          })),
        );

        setBanners(
          (details.banners || []).map((img) => ({
            key: img,
            preview: img,
          })),
        );

        // Video preview (single video supported)
        if (original.youtubeUrls.length > 0) {
          const yt = original.youtubeUrls[0];

          setVideo({
            type: "youtube",
            url: yt,
            videoId: getYoutubeID(yt),
          });
        } else if (original.videos.length > 0) {
          const key = original.videos[0];
          const videoObj = details.videos?.map((video) => video);

          setVideo({
            type: "file",
            key,
            preview: videoObj,
          });
        }
      }
    } catch (err) {
      console.error("fetchStep5Media error", err);
    }
  };

  /* ---------------- SUBMIT ---------------- */

  const isSameArray = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    return [...a].sort().join("|") === [...b].sort().join("|");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showLoader();

    try {
      const currentPayload = {
        images: images.map((i) => i.key),
        banners: banners.map((i) => i.key),
        videos: video?.type === "file" ? [video.key] : [],
        youtubeUrls: video?.type === "youtube" ? [video.url] : [],
      };

      console.log(currentPayload);

      const original = originalMediaRef.current;

      const imagesChanged = !isSameArray(
        original?.images,
        currentPayload.images,
      );
      const bannersChanged = !isSameArray(
        original?.banners,
        currentPayload.banners,
      );
      const videosChanged = !isSameArray(
        original?.videos,
        currentPayload.videos,
      );
      const youtubeChanged = !isSameArray(
        original?.youtubeUrls,
        currentPayload.youtubeUrls,
      );

      if (
        !imagesChanged &&
        !bannersChanged &&
        !videosChanged &&
        !youtubeChanged
      ) {
        hideLoader();
        nextStep(currentPayload);
        return;
      }

      const payload = {};
      if (imagesChanged) payload.images = currentPayload.images;
      if (bannersChanged) payload.banners = currentPayload.banners;
      if (videosChanged) payload.videos = currentPayload.videos;
      if (youtubeChanged) payload.youtubeUrls = currentPayload.youtubeUrls;

      const res = await ProductAPI.updateStep5MediaVideo(productId, payload);

      if (res.code === 200) {
        showSuccess(res.message);
        nextStep(payload);
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h4>Media & Video</h4>
      </div>
      <div className="card-body">
        <form method="post" onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-lg-12">
              <label className="form-label">Images</label>
              <div className="add-choosen">
                <div className="mb-3 image-upload image-upload-two">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="imageInput"
                    style={{ display: "none" }}
                  />
                  <label htmlFor="imageInput" className="image-uploads">
                    <i className="ti ti-plus" />
                    <h4>Add Images</h4>
                  </label>
                </div>

                {images.map((imgObj, idx) => (
                  <div
                    className="phone-img position-relative me-3 mb-3"
                    key={idx}
                    style={{ width: 120 }}
                  >
                    <img
                      src={imgObj.preview || ""}
                      alt={`preview-${idx}`}
                      className="img-fluid"
                      style={{ borderRadius: 4 }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute top-0 end-0"
                      onClick={() => removeImage(idx)}
                      style={{ transform: "translate(50%,-50%)" }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-lg-12">
              <label className="form-label">
                Other Description Related To The Product
              </label>
              <div className="add-choosen">
                <div className="mb-3 image-upload image-upload-two">
                  <input
                    type="file"
                    multiple
                    onChange={handleBannerUpload}
                    id="bannerInput"
                    style={{ display: "none" }}
                  />
                  <label htmlFor="bannerInput" className="image-uploads">
                    <i className="ti ti-plus" />
                    <h4>Add File</h4>
                  </label>
                </div>

                {banners.map((imgObj, idx) => {
                  const isPDF =
                    imgObj.fileType?.includes("pdf") ||
                    imgObj.key?.toLowerCase().endsWith(".pdf");

                  return (
                    <div
                      className="phone-img position-relative me-3 mb-3"
                      key={idx}
                      style={{ width: 120 }}
                    >
                      {isPDF ? (
                        <a
                          href={imgObj.preview}
                          target="_blank"
                          rel="noreferrer"
                          className="d-flex flex-column align-items-center justify-content-center text-decoration-none bg-light border text-danger"
                          style={{
                            width: 120,
                            height: 120,
                            borderRadius: 4,
                            padding: 5,
                          }}
                        >
                          <span
                            style={{ fontSize: "2rem", fontWeight: "bold" }}
                          >
                            PDF
                          </span>
                          <span
                            className="text-dark text-center mt-1"
                            style={{
                              fontSize: "10px",
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {imgObj.name || "View PDF"}
                          </span>
                        </a>
                      ) : (
                        <img
                          src={imgObj.preview}
                          alt={`preview-${idx}`}
                          className="img-fluid"
                          style={{
                            borderRadius: 4,
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                          }}
                        />
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-light position-absolute top-0 end-0"
                        onClick={() => removeBanner(idx)}
                        style={{ transform: "translate(50%,-50%)", zIndex: 10 }}
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <hr />
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Upload Video</label>
              <input
                type="file"
                accept="video/*"
                className="form-control mb-2"
                onChange={handleVideoUpload}
                disabled={video?.type === "youtube"}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Or Paste YouTube URL</label>
              <input
                type="text"
                className="form-control"
                placeholder="https://youtu.be/..."
                value={video?.type === "youtube" ? video.url : ""}
                onChange={handleYoutubeChange}
                disabled={video?.type === "file"}
              />
            </div>
          </div>

          {video?.type === "file" && (
            <div className="mt-3">
              <video
                controls
                src={video.preview || ""}
                style={{ width: "100%", height: 320 }}
              />
              <button
                type="button"
                className="btn btn-sm btn-danger mt-2"
                onClick={removeVideo}
              >
                Remove Video
              </button>
            </div>
          )}

          {loading && (
            <div className="mt-3">
              <p>Uploading video, please wait...</p>
            </div>
          )}

          {video?.type === "youtube" && (
            <div className="mt-3">
              <iframe
                height="320"
                width="100%"
                src={`https://www.youtube.com/embed/${video.videoId}`}
                allowFullScreen
                title="YouTube Preview"
              ></iframe>
              <button
                type="button"
                className="btn btn-sm btn-danger mt-2"
                onClick={removeVideo}
              >
                Remove Video
              </button>
            </div>
          )}

          <div className="row mt-3">
            <div className="col-md-12">
              <button
                type="submit"
                id="submitBtn"
                className="me-2 btn btn-primary shadow-none"
                onClick={prevStep}
              >
                Prev
              </button>
              <button
                type="submit"
                id="submitBtn"
                className="btn btn-primary shadow-none"
              >
                Next
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default Step5Media;
